import React, { Component } from 'react';
import { array, arrayOf, bool, func, number, object, shape, string } from 'prop-types';
import pickBy from 'lodash/pickBy';
import classNames from 'classnames';

import appSettings from '../../../config/settings';
import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';

import { FormattedMessage, intlShape, useIntl } from '../../../util/reactIntl';
import { isMainSearchTypeKeywords, isOriginInUse } from '../../../util/search';
import { parse, stringify } from '../../../util/urlHelpers';
import { createResourceLocatorString, matchPathname, pathByRouteName } from '../../../util/routes';
import { propTypes } from '../../../util/types';
import { trackEvent } from '../../../util/analytics';
import { boltCircleSpellings } from '../../../util/boltCircleCore';
import {
  Avatar,
  Button,
  CartMenu,
  LimitedAccessBanner,
  LinkedLogo,
  Modal,
  ModalMissingInformation,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
  SearchField,
} from '../../../components';

import MenuIcon from './MenuIcon';
import SearchIcon from './SearchIcon';
import TopbarSearchForm from './TopbarSearchForm/TopbarSearchForm';
import TopbarMobileMenu from './TopbarMobileMenu/TopbarMobileMenu';
import TopbarDesktop from './TopbarDesktop/TopbarDesktop';
import { useDispatch } from 'react-redux';

import css from './Topbar.module.css';
import { Link, useHistory, useLocation } from 'react-router-dom';
// Desktop topbar renders the full lockup via <LinkedLogo />; mobile uses
// the symbol-only mark so the 56px header has room for the "+ Annons"
// primary CTA without the wordmark overlapping on narrow phones.
import wheelplaceSymbol from '../../../assets/wheelplace-symbol.svg';

import { ReactComponent as NotificationIcon } from '../../TopbarContainer/Topbar/TopbarDesktop/Bell.svg';
import { markNotificationAsRead } from '../../../ducks/user.duck';

const Box = ({ sx, children, ...rest }) => (
  <div {...rest} style={{ ...(sx || {}), ...(rest.style || {}) }}>
    {children}
  </div>
);
const ChatBubbleOutlineIcon = ({ sx, style }) => {
  const size = sx?.fontSize || 20;
  const color = sx?.color || 'currentColor';
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
      aria-hidden="true"
    >
      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
    </svg>
  );
};
const ReceiptLongIcon = ({ sx, style }) => {
  const size = sx?.fontSize || 20;
  const color = sx?.color || 'currentColor';
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={color}
      style={style}
      aria-hidden="true"
    >
      <path d="M19.5 3.5L18 2l-1.5 1.5L15 2l-1.5 1.5L12 2l-1.5 1.5L9 2 7.5 3.5 6 2v16H3v3c0 1.66 1.34 3 3 3h12c1.66 0 3-1.34 3-3V2l-1.5 1.5zM19 21c0 .55-.45 1-1 1s-1-.45-1-1v-3H8V5h11v16zM9 7h6v2H9zm8 0h2v2h-2zm-8 4h6v2H9zm8 0h2v2h-2z" />
    </svg>
  );
};

const MAX_MOBILE_SCREEN_WIDTH = 1440;

/* P25009-443 BR-3: the registration-plate / text search bar belongs on the
 * buyer surfaces — Annonser (all search routes), the listing, the shop page,
 * the SEO landing pages and public content. On account pages it was 40px of
 * stolen height on every screen. Matched by prefix because the listing,
 * search and profile routes come in families (ListingPageVariant,
 * SearchPageMake, ProfilePageSlugged, CMSPage:pageId, Seo*...). */
const SEARCH_BAR_PAGE_PATTERN = /^(LandingPage|SearchPage|ListingPage|ProfilePage|CMSPage|Seo|TireDimensionPage|AllBrandsPage|AISearchPage)/;
const showSearchBarOnPage = resolvedCurrentPage =>
  !resolvedCurrentPage || SEARCH_BAR_PAGE_PATTERN.test(resolvedCurrentPage);

const redirectToURLWithModalState = (props, modalStateParam) => {
  const { history, location } = props;
  const { pathname, search, state } = location;
  const searchString = `?${stringify({ [modalStateParam]: 'open', ...parse(search) })}`;
  history.push(`${pathname}${searchString}`, state);
};

const redirectToURLWithoutModalState = (props, modalStateParam) => {
  const { history, location } = props;
  const { pathname, search, state } = location;
  const queryParams = pickBy(parse(search), (v, k) => {
    return k !== modalStateParam;
  });
  const stringified = stringify(queryParams);
  const searchString = stringified ? `?${stringified}` : '';
  history.push(`${pathname}${searchString}`, state);
};

const isPrimary = o => o.group === 'primary';
const isSecondary = o => o.group === 'secondary';
const compareGroups = (a, b) => {
  const isAHigherGroupThanB = isPrimary(a) && isSecondary(b);
  const isALesserGroupThanB = isSecondary(a) && isPrimary(b);
  // Note: sort order is stable in JS
  return isAHigherGroupThanB ? -1 : isALesserGroupThanB ? 1 : 0;
};
// Returns links in order where primary links are returned first
const sortCustomLinks = customLinks => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.sort(compareGroups);
};

// Resolves in-app links against route configuration
const getResolvedCustomLinks = (customLinks, routeConfiguration) => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.map(linkConfig => {
    const { type, href } = linkConfig;
    const isInternalLink = type === 'internal' || href.charAt(0) === '/';
    if (isInternalLink) {
      // Internal link
      const testURL = new URL('http://my.marketplace.com' + href);
      const matchedRoutes = matchPathname(testURL.pathname, routeConfiguration);
      if (matchedRoutes.length > 0) {
        const found = matchedRoutes[0];
        const to = { search: testURL.search, hash: testURL.hash };
        return {
          ...linkConfig,
          route: {
            name: found.route?.name,
            params: found.params,
            to,
          },
        };
      }
    }
    return linkConfig;
  });
};

const isCMSPage = found =>
  found.route?.name === 'CMSPage' ? `CMSPage:${found.params?.pageId}` : null;
const isInboxPage = found =>
  found.route?.name === 'InboxPage' ? `InboxPage:${found.params?.tab}` : null;
// Find the name of the current route/pathname.
// It's used as handle for currentPage check.
const getResolvedCurrentPage = (location, routeConfiguration) => {
  const matchedRoutes = matchPathname(location.pathname, routeConfiguration);
  if (matchedRoutes.length > 0) {
    const found = matchedRoutes[0];
    const cmsPageName = isCMSPage(found);
    const inboxPageName = isInboxPage(found);
    return cmsPageName ? cmsPageName : inboxPageName ? inboxPageName : `${found.route?.name}`;
  }
};

const GenericError = props => {
  const { show } = props;
  const classes = classNames(css.genericError, {
    [css.genericErrorVisible]: show,
  });
  return (
    <div className={classes}>
      <div className={css.genericErrorContent}>
        <p className={css.genericErrorText}>
          <FormattedMessage id="Topbar.genericError" />
        </p>
      </div>
    </div>
  );
};

GenericError.propTypes = {
  show: bool.isRequired,
};

const MobileModeToggle = () => {
  const history = useHistory();
  const location = useLocation();
  const isInboxOrTransactionPage = 
    location.pathname.includes('/inbox') || 
    location.pathname.includes('/order') || 
    location.pathname.includes('/sale');

  const [mode, setMode] = React.useState(() => {
    if (typeof window !== 'undefined') {
      return window.localStorage.getItem('buyerSellerMode') || 'buyer';
    }
    return 'buyer';
  });

  if (!isInboxOrTransactionPage) return null;

  const handleToggle = (role) => {
    if (mode !== role) {
      if (location.pathname.includes('/inbox')) {
        history.push(`/inbox/${role === 'seller' ? 'sales' : 'orders'}`);
      } else if (location.pathname.includes('/order') || location.pathname.includes('/sale')) {
        history.push(role === 'seller' ? '/sale' : '/order');
      }
      setMode(role);
      window.localStorage.setItem('buyerSellerMode', role);
    }
  };

  const isSeller = mode === 'seller';
  const isBuyer = mode === 'buyer';

  return (
    <div className={css.mobileToggleRow}>
      <div className={css.mobileToggleContainer}>
        <button
          className={classNames(css.mobileToggleButton, { [css.activeMobileToggle]: isSeller })}
          onClick={() => handleToggle('seller')}
        >
          <FormattedMessage id="Sidebar.seller" />
        </button>
        <button
          className={classNames(css.mobileToggleButton, { [css.activeMobileToggle]: isBuyer })}
          onClick={() => handleToggle('buyer')}
        >
          <FormattedMessage id="Sidebar.buyer" />
        </button>
      </div>
    </div>
  );
};

class TopbarComponent extends Component {
  constructor(props) {
    super(props);
    this.handleMobileMenuOpen = this.handleMobileMenuOpen.bind(this);
    this.handleMobileMenuClose = this.handleMobileMenuClose.bind(this);
    this.handleMobileSearchOpen = this.handleMobileSearchOpen.bind(this);
    this.handleMobileSearchClose = this.handleMobileSearchClose.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleLogout = this.handleLogout.bind(this);
  }

  handleMobileMenuOpen() {
    redirectToURLWithModalState(this.props, 'mobilemenu');
  }

  handleMobileMenuClose() {
    redirectToURLWithoutModalState(this.props, 'mobilemenu');
  }

  handleMobileSearchOpen() {
    redirectToURLWithModalState(this.props, 'mobilesearch');
  }

  handleMobileSearchClose() {
    redirectToURLWithoutModalState(this.props, 'mobilesearch');
  }

  handleSubmit(values) {
    const { currentSearchParams } = this.props;
    const { history, config, routeConfiguration } = this.props;

    const urlParams = new URLSearchParams(window.location.search);

    const persistentParams = {};
    ['authorId', 'showByUser', 'selectedUserId'].forEach(key => {
      if (urlParams.has(key)) {
        persistentParams[key] = urlParams.get(key);
      }
    });

    const topbarSearchParams = () => {
      if (isMainSearchTypeKeywords(config)) {
        // Rim bolt pattern suggestion
        if (values.numberOfBolts && values.boltCircle) {
          return {
            pub_numberOfBolts: values.numberOfBolts,
            // Every stored spelling of the circle (P25009-488).
            pub_boltCircle: boltCircleSpellings(values.boltCircle).join(',') || values.boltCircle,
            pub_adType: values.adType || 'rim',
            keywords: undefined,
            pub_categoryLevel1: undefined,
            pub_categoryLevel2: undefined,
            pub_tireWidthFront: undefined,
            pub_profileFront: undefined,
            pub_diameter: undefined,
          };
        }

        // Rim diameter suggestion
        if (values.diameter && values.adType === 'rim') {
          return {
            pub_diameter: values.diameter,
            pub_adType: values.adType,
            keywords: undefined,
            pub_categoryLevel1: undefined,
            pub_categoryLevel2: undefined,
            pub_tireWidthFront: undefined,
            pub_profileFront: undefined,
            pub_numberOfBolts: undefined,
            pub_boltCircle: undefined,
          };
        }

        // Rim brand suggestion
        if (values.categoryLevel1 && values.adType === 'rim') {
          return {
            pub_categoryLevel1: values.categoryLevel1,
            pub_categoryLevel2: values.categoryLevel2 || '',
            pub_adType: values.adType,
            keywords: undefined,
            pub_tireWidthFront: undefined,
            pub_profileFront: undefined,
            pub_diameter: undefined,
            pub_numberOfBolts: undefined,
            pub_boltCircle: undefined,
          };
        }

        // Tire dimension suggestion
        if (values.tireWidthFront && values.profileFront && values.diameter) {
          return {
            pub_tireWidthFront: values.tireWidthFront,
            pub_profileFront: values.profileFront,
            pub_diameter: values.diameter,
            keywords: undefined,
            pub_categoryLevel1: undefined,
            pub_categoryLevel2: undefined,
            pub_adType: undefined,
            pub_numberOfBolts: undefined,
            pub_boltCircle: undefined,
          };
        }

        // Category / subcategory suggestion
        if (values.categoryLevel1) {
          return {
            pub_categoryLevel1: values.categoryLevel1,
            pub_categoryLevel2: (values.categoryLevel2 || ''),
            keywords: undefined,
            pub_tireWidthFront: undefined,
            pub_profileFront: undefined,
            pub_diameter: undefined,
            pub_adType: undefined,
            pub_numberOfBolts: undefined,
            pub_boltCircle: undefined,
          };
        }

        // Plain keyword search
        return {
          keywords: values?.keywords,
          pub_categoryLevel1: undefined,
          pub_categoryLevel2: undefined,
          pub_tireWidthFront: undefined,
          pub_profileFront: undefined,
          pub_diameter: undefined,
          pub_adType: undefined,
          pub_numberOfBolts: undefined,
          pub_boltCircle: undefined,
        };
      }

      // topbar search defaults to 'location' search
      const { search, selectedPlace } = values?.location;
      const { origin, bounds } = selectedPlace;
      const originMaybe = isOriginInUse(config) ? { origin } : {};

      return {
        ...originMaybe,
        address: search,
        bounds,
      };
    };
    const searchParams = {
      ...persistentParams,
      ...currentSearchParams,
      ...topbarSearchParams(),
    };
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, searchParams));
  }

  handleLogout() {
    const { onLogout, history, routeConfiguration } = this.props;
    onLogout().then(() => {
      const path = pathByRouteName('LandingPage', routeConfiguration);

      // In production we ensure that data is really lost,
      // but in development mode we use stored values for debugging
      if (appSettings.dev) {
        history.push(path);
      } else if (typeof window !== 'undefined') {
        window.location = path;
      }

      console.log('logged out'); // eslint-disable-line
    });
  }

  render() {
    const {
      className,
      rootClassName,
      desktopClassName,
      mobileRootClassName,
      mobileClassName,
      isAuthenticated,
      isLoggedInAs,
      authScopes,
      authInProgress,
      currentUser,
      currentUserHasListings,
      currentUserHasOrders,
      currentPage,
      notificationCount,
      eventCount,
      intl,
      location,
      onManageDisableScrolling,
      onResendVerificationEmail,
      sendVerificationEmailInProgress,
      sendVerificationEmailError,
      showGenericError,
      config,
      routeConfiguration,
      notifications,
      dispatch,
    } = this.props;

    const { mobilemenu, mobilesearch, keywords, address, origin, bounds } = parse(location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });

    // Custom links are sorted so that group="primary" are always at the beginning of the list.
    const sortedCustomLinks = sortCustomLinks(config.topbar?.customLinks);
    const customLinks = getResolvedCustomLinks(sortedCustomLinks, routeConfiguration);
    const resolvedCurrentPage = currentPage || getResolvedCurrentPage(location, routeConfiguration);

    const showMobileSearchButton = resolvedCurrentPage !== 'SearchPage';


    const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

    const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
    const isMobileLayout = hasMatchMedia
      ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
      : true;
    const isMobileMenuOpen = isMobileLayout && mobilemenu === 'open';
    const isMobileSearchOpen = isMobileLayout && mobilesearch === 'open';

    /* BR-6 / AC-5 — the bell counts EVENTS.
     *
     * It counted `notifications.filter(n => n.isUnread)`, which mixes unread
     * messages with every unopened sale, so the bell and the envelope reported
     * overlapping and contradictory numbers. `eventCount` is computed once in
     * TopbarContainer from the same notification list, so the mobile and desktop
     * bells cannot drift apart either — they used to each do their own arithmetic
     * on the same array. */
    const notificationsMenu = isAuthenticated ? (
      <Menu>
        <MenuLabel className={css.notificationMenuLabel}>
          <div className={css.bellWrapper}>
            <NotificationIcon />
            {eventCount > 0 && (
              <div className={css.notificationBadge}>{eventCount}</div>
            )}
          </div>
        </MenuLabel>
        <MenuContent className={css.notificationMenuContent}>
          {notifications.length > 0 ? (
            notifications.map(n => {
              // sortDate is set per notification in fetchNotificationData
              // (last message date for messages, createdAt for activity).
              const createdAt = n.sortDate || n.attributes.createdAt;
              const date = createdAt ? new Date(createdAt).toLocaleString('en-GB', {
                month: 'short',
                day: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
              }).replace(',', '') : null;
              return (
                <MenuItem key={n.id.uuid} className={css.notificationMenuItem}>
                  <Link
                    to={n.url}
                    className={classNames(
                      css.notificationItem,
                      n.isUnread ? css.notificationItemUnread : css.notificationItemRead
                    )}
                    onClick={() => dispatch(markNotificationAsRead(n.id.uuid))}
                  >
                    <div className={css.notificationIcon}>
                      {n.notificationType === 'MESSAGE' ? (
                        <ChatBubbleOutlineIcon sx={{ fontSize: 20, color: '#1E2F7B' }} />
                      ) : (
                        <ReceiptLongIcon sx={{ fontSize: 20, color: '#477bf4' }} />
                      )}
                    </div>
                    <div className={css.notificationText}>
                      <p className={css.notificationTitle}>{n.notificationTitle}</p>
                      {date && <p className={css.notificationDate}>{date}</p>}
                    </div>
                  </Link>
                </MenuItem>
              );
            })
          ) : (
            /* FIX: Wrap the empty state in a MenuItem to satisfy the strict requirement */
            <MenuItem key="no-notifications">
              <div className={css.noNotifications}>
                <FormattedMessage id="TopbarDesktop.noNotifications" defaultMessage="No new notifications" />
              </div>
            </MenuItem>
          )}
        </MenuContent>
      </Menu>
    ) : null;

    const mobileMenu = (
      <TopbarMobileMenu
        isAuthenticated={isAuthenticated}
        currentUserHasListings={currentUserHasListings}
        currentUser={currentUser}
        onLogout={this.handleLogout}
        notificationCount={notificationCount}
        currentPage={resolvedCurrentPage}
        customLinks={customLinks}
      />
    );

    const topbarSearcInitialValues = () => {
      if (isMainSearchTypeKeywords(config)) {
        return { keywords };
      }

      // Only render current search if full place object is available in the URL params
      const locationFieldsPresent = isOriginInUse(config)
        ? address && origin && bounds
        : address && bounds;
      return {
        location: locationFieldsPresent
          ? {
            search: address,
            selectedPlace: { address, origin, bounds },
          }
          : null,
      };
    };
    const initialSearchFormValues = topbarSearcInitialValues();

    const classes = classNames(rootClassName || css.root, className);

    return (
      <div className={classes}>
        <LimitedAccessBanner
          isAuthenticated={isAuthenticated}
          isLoggedInAs={isLoggedInAs}
          authScopes={authScopes}
          currentUser={currentUser}
          onLogout={this.handleLogout}
          currentPage={resolvedCurrentPage}
        />
        <div className={classNames(mobileRootClassName || css.container, mobileClassName)}>
          <Button
            rootClassName={css.menu}
            onClick={this.handleMobileMenuOpen}
            title={intl.formatMessage({ id: 'Topbar.menuIcon' })}
          >
            <MenuIcon className={css.menuIcon} />
            {notificationDot}
          </Button>
          <Link to="/" className={css.mobileLogoLink} aria-label="Wheelplace — startsidan">
            <img
              className={css.mobileLogo}
              alt="Wheelplace"
              src={wheelplaceSymbol}
            />
          </Link>
          {/* Mobile action cluster (spec §3, mobile):
           *   [search icon] [+ Annons primary] [avatar or login]
           * — search icon opens the full-screen search overlay (now a
           *     <SearchField size="full" /> with REG/Text toggle)
           *   — "+ Annons" is the only always-visible primary CTA
           *   — avatar (auth) or user-icon login link replaces the old
           *     notifications-only cluster */}
          <div className={css.actionsWrapper}>
            {/* Mobile search trigger removed — the "+ Annons" primary
              * CTA + avatar/login are the only actions on the mobile
              * header now. The full-screen search overlay + modal
              * plumbing is still wired (handleMobileSearchOpen stays
              * available), so restoring the trigger is a one-line
              * revert if we want it back. */}

            {/* P25009-443 (spec rule 3): the "+ Annons" button was crowded
              * and truncated between the burger and four icons. A primary
              * action that cannot fit in the header belongs in the bottom
              * bar — the drawer's pinned footer and the page action bars
              * carry "Lägg in annons" at full width now. */}

            {isAuthenticated ? (
              <div className={css.mobileBell}>{notificationsMenu}</div>
            ) : null}
            <CartMenu iconClassName={css.mobileCartSlot} />
            {isAuthenticated ? (
              <NamedLink name="ProfileSettingsPage" className={css.mobileAvatar}>
                <Avatar user={currentUser} disableProfileLink className={css.mobileAvatarImg} />
                {notificationDot}
              </NamedLink>
            ) : (
              <NamedLink
                name="LoginPage"
                className={css.mobileLogin}
                aria-label={intl.formatMessage({ id: 'TopbarDesktop.login' })}
                onClick={() => trackEvent('login_open', { trigger: 'header_mobile' })}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
                  />
                </svg>
              </NamedLink>
            )}
          </div>


        </div>

        {/* BR-3: buyer surfaces only — see showSearchBarOnPage. */}
        {showSearchBarOnPage(resolvedCurrentPage) ? (
          <div className={css.mobileSearchRow}>
            <SearchField
              size="compact"
              onSearchSubmit={this.handleSubmit}
              initialSearchFormValues={initialSearchFormValues}
              appConfig={config}
            />
          </div>
        ) : null}

        <MobileModeToggle />

        <div className={css.desktop}>
          <TopbarDesktop
            className={desktopClassName}
            currentUserHasListings={currentUserHasListings}
            currentUser={currentUser}
            currentPage={resolvedCurrentPage}
            initialSearchFormValues={initialSearchFormValues}
            intl={intl}
            isAuthenticated={isAuthenticated}
            notificationCount={notificationCount}
            eventCount={eventCount}
            onLogout={this.handleLogout}
            onManageDisableScrolling={onManageDisableScrolling}
            onSearchSubmit={this.handleSubmit}
            appConfig={config}
            customLinks={customLinks}
            notifications={notifications}
          />
        </div>

        {/* Category strip removed from listing routes — product type and
          * season are now driven by the DimensionQuickSearch toggles in
          * the SearchPage filter sidebar. Kept on non-listing routes. */}
        <Modal
          id="TopbarMobileMenu"
          containerClassName={css.modalContainer}
          scrollLayerClassName={css.mobileMenuScrollLayer}
          isOpen={isMobileMenuOpen}
          onClose={this.handleMobileMenuClose}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
          /* Suppress the "STÄNG" label — a universal X icon is
           * calmer and takes less space. Passing a truthy-but-empty
           * node short-circuits the Modal's FormattedMessage fallback. */
          closeButtonMessage={<span aria-hidden="true" />}
        >
          {authInProgress ? null : mobileMenu}
        </Modal>
        <Modal
          id="TopbarMobileSearch"
          containerClassName={css.modalContainerSearchForm}
          isOpen={isMobileSearchOpen}
          onClose={this.handleMobileSearchClose}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <div className={css.searchContainer}>
            <SearchField
              size="full"
              onSearchSubmit={this.handleSubmit}
              initialSearchFormValues={initialSearchFormValues}
              appConfig={config}
            />
            <p className={css.mobileHelp}>
              <FormattedMessage id="Topbar.mobileSearchHelp" />
            </p>
          </div>
        </Modal>
        <ModalMissingInformation
          id="MissingInformationReminder"
          containerClassName={css.missingInformationModal}
          currentUser={currentUser}
          currentUserHasListings={currentUserHasListings}
          currentUserHasOrders={currentUserHasOrders}
          location={location}
          onManageDisableScrolling={onManageDisableScrolling}
          onResendVerificationEmail={onResendVerificationEmail}
          sendVerificationEmailInProgress={sendVerificationEmailInProgress}
          sendVerificationEmailError={sendVerificationEmailError}
        />

        <GenericError show={showGenericError} />
      </div>
    );
  }
}

TopbarComponent.defaultProps = {
  className: null,
  rootClassName: null,
  desktopClassName: null,
  mobileRootClassName: null,
  mobileClassName: null,
  notificationCount: 0,
  currentUser: null,
  currentUserHasOrders: null,
  currentPage: null,
  sendVerificationEmailError: null,
  authScopes: [],
};

TopbarComponent.propTypes = {
  className: string,
  rootClassName: string,
  desktopClassName: string,
  mobileRootClassName: string,
  mobileClassName: string,
  isAuthenticated: bool.isRequired,
  isLoggedInAs: bool.isRequired,
  authScopes: array,
  authInProgress: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentUserHasListings: bool.isRequired,
  currentUserHasOrders: bool,
  currentPage: string,
  notificationCount: number,
  onLogout: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  onResendVerificationEmail: func.isRequired,
  sendVerificationEmailInProgress: bool.isRequired,
  sendVerificationEmailError: propTypes.error,
  showGenericError: bool.isRequired,

  // These are passed from Page to keep Topbar rendering aware of location changes
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string.isRequired,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,
};

const Topbar = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const dispatch = useDispatch();

  return (
    <TopbarComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      dispatch={dispatch}
      {...props}
    />
  );
};

export default Topbar;
